import { useCallback, useEffect, useRef, useState } from 'react'

/**
 * Guards an async download/export click against rage-clicks.
 *
 * While the triggered async action is in flight, `loading` is true and
 * `onClick` becomes a no-op, so the underlying request/download fires at most
 * once no matter how many times the user clicks.
 *
 * Usage:
 *   const download = useDownloadGuard(handleDownloadReport)
 *   <Button loading={download.loading} onClick={download.onClick} />
 *
 * IMPORTANT: the wrapped handler MUST return the promise that settles when the
 * download finishes (e.g. `return axios.post(...)` or `return dispatch(thunk())`).
 * If it returns undefined the guard releases on the next microtask and cannot
 * block the second click.
 *
 * @param {(...args:any[]) => (Promise<any>|any)} handler async click handler
 * @param {{ disabled?: boolean }} [options] pass an external disabled flag to merge
 * @returns {{ loading: boolean, disabled: boolean, onClick: (...args:any[]) => Promise<void> }}
 */
export default function useDownloadGuard(handler, { disabled = false } = {}) {
  const [loading, setLoading] = useState(false)
  const inFlight = useRef(false)
  const mounted = useRef(true)

  useEffect(
    () => () => {
      mounted.current = false
    },
    [],
  )

  const onClick = useCallback(
    async (...args) => {
      if (inFlight.current) return
      inFlight.current = true
      setLoading(true)
      try {
        await handler?.(...args)
      } finally {
        inFlight.current = false
        if (mounted.current) setLoading(false)
      }
    },
    [handler],
  )

  return { loading, disabled: disabled || loading, onClick }
}
